import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, API, getToken } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Sparkles, Settings2, FileText, Mic, Square, Volume2, StopCircle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter
} from '../components/ui/dialog';

const AI_AVATAR = "https://images.unsplash.com/photo-1669154477372-12c178bfe936?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxnbG93aW5nJTIwb3JiJTIwbWluaW1hbCUyMHNvZnQlMjBsaWdodHxlbnwwfHx8fDE3NzY5NjE0NDR8MA&ixlib=rb-4.1.0&q=85";

export default function ChatView() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const { t } = useT();
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [personas, setPersonas] = useState([]);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [newPersona, setNewPersona] = useState('samtalepartner');
    const [newDepth, setNewDepth] = useState(2);
    const [limitReached, setLimitReached] = useState(false);
    const [summary, setSummary] = useState(null);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const scrollRef = useRef(null);

    // Copy-protect AI replies for free + trial users to deter feeding into other LLMs.
    // Paying users (basic / premium) keep full copy access on their own data.
    const copyProtected = !user || !['basic', 'premium'].includes(user?.plan);

    const load = async () => {
        try {
            const { data } = await api.get(`/chat/sessions/${sessionId}`);
            setSession(data.session);
            setMessages(data.messages);
            setNewPersona(data.session.persona);
            setNewDepth(data.session.depth || 2);
            setSummary(data.session.summary);
        } catch {
            toast.error(t('chat.load.error'));
            navigate('/app/samtaler');
        }
    };

    useEffect(() => { load(); api.get('/personas').then(r => setPersonas(r.data)); /* eslint-disable-next-line */ }, [sessionId]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);

    const send = async () => {
        const content = text.trim();
        if (!content || sending) return;
        if (!user?.is_premium && (user?.message_count ?? 0) >= (user?.free_limit ?? 10)) {
            setLimitReached(true); return;
        }
        setSending(true);
        setMessages(m => [...m, { message_id: 'tmp', role: 'user', content, created_at: new Date().toISOString() }]);
        setText('');
        try {
            const { data } = await api.post(`/chat/sessions/${sessionId}/messages`, { content });
            await refresh();
            setMessages(m => [...m.filter(x => x.message_id !== 'tmp'), data.user_message, data.ai_message]);
            if (data.persona_switched_to) {
                // Reload session so header + personaKey reflect the new style
                const fresh = await api.get(`/chat/sessions/${sessionId}`);
                setSession(fresh.data.session);
                setNewPersona(fresh.data.session.persona);
                const label = personas.find(p => p.key === data.persona_switched_to)?.label || '';
                toast.success(`${t('chat.style.changed')}${label ? ': ' + label : ''}`);
            }
            if (data.limit_reached) setLimitReached(true);
        } catch (e) {
            setMessages(m => m.filter(x => x.message_id !== 'tmp'));
            if (e.response?.status === 402) setLimitReached(true);
            else toast.error(e.response?.data?.detail || t('chat.send.error'));
        } finally {
            setSending(false);
        }
    };

    const applySettings = async () => {
        await api.patch(`/chat/sessions/${sessionId}`, { persona: newPersona, depth: newDepth });
        setSettingsOpen(false);
        toast.success(t('chat.updated'));
        load();
    };

    const makeSummary = async () => {
        setSummaryOpen(true);
        setSummary(t('chat.summary.generating'));
        try {
            const { data } = await api.post(`/chat/sessions/${sessionId}/summary`);
            setSummary(data.summary);
        } catch {
            setSummary(t('chat.summary.error'));
        }
    };

    const upgrade = async () => {
        await api.post('/premium/upgrade');
        await refresh();
        setLimitReached(false);
        toast.success('Premium aktivert (demo)');
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                if (blob.size < 500) { setTranscribing(false); return; }
                setTranscribing(true);
                const fd = new FormData();
                fd.append('file', blob, 'recording.webm');
                try {
                    const { data } = await api.post(
                        `/voice/transcribe?language=${session?.language || 'no'}`,
                        fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    setText(prev => (prev ? prev + ' ' : '') + (data.text || '').trim());
                } catch (e) {
                    toast.error(e.response?.data?.detail || t('chat.transcribe.error'));
                } finally {
                    setTranscribing(false);
                }
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setRecording(true);
        } catch (e) {
            toast.error(t('chat.mic.denied'));
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
    };

    const currentPersona = personas.find(p => p.key === session?.persona);

    return (
        <div className="flex flex-col h-screen md:h-[100vh]" data-testid="chat-view">
            {/* Header */}
            <div className="border-b border-sand bg-bone/90 backdrop-blur-sm px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-20">
                <button onClick={() => navigate('/app/samtaler')} className="flex items-center gap-2 text-sm text-inkSoft hover:text-ink" data-testid="back-to-chats">
                    <ArrowLeft className="w-4 h-4"/> <span className="hidden sm:inline">{t('chat.back')}</span>
                </button>
                <div className="text-center">
                    <p className="text-xs text-inkSoft">{currentPersona?.emoji} {currentPersona?.label}</p>
                </div>
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={makeSummary} data-testid="summary-btn" className="text-inkSoft hover:text-ink">
                        <FileText className="w-4 h-4"/>
                    </Button>
                    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                        <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid="chat-settings-btn" className="text-inkSoft hover:text-ink">
                                <Settings2 className="w-4 h-4"/>
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-bone max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-display">{t('chat.adjust.title')}</DialogTitle>
                                <DialogDescription className="sr-only">{t('chat.adjust.desc')}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-2">
                                <div>
                                    <p className="text-sm font-medium text-ink mb-2">{t('chat.style')}</p>
                                    <div className="grid grid-cols-1 gap-2">
                                        {personas.map(p => (
                                            <button key={p.key} onClick={() => setNewPersona(p.key)}
                                                className={`relative p-3 rounded-xl border-2 text-left text-sm transition-all ${newPersona === p.key ? 'border-sage bg-sage text-bone' : 'border-sand bg-white'}`}
                                                data-testid={`switch-persona-${p.key}`}>
                                                <div className="flex items-start gap-2">
                                                    <div className="text-lg shrink-0">{p.emoji}</div>
                                                    <div className="flex-1">
                                                        <div className={`font-medium ${newPersona === p.key ? 'text-bone' : 'text-ink'}`}>{p.label}</div>
                                                        <div className={`text-xs mt-0.5 ${newPersona === p.key ? 'text-bone/80' : 'text-inkSoft'}`}>{p.description}</div>
                                                    </div>
                                                </div>
                                                {newPersona === p.key && (
                                                    <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-bone flex items-center justify-center text-sage-dark text-[10px] font-bold">✓</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-baseline justify-between mb-1">
                                        <p className="text-sm font-medium text-ink">{t('chat.depth')}</p>
                                        <p className="text-xs text-inkSoft" data-testid="chat-depth-label">{[t('chat.depth.1'), t('chat.depth.2'), t('chat.depth.3'), t('chat.depth.4')][newDepth-1]}</p>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="4"
                                        step="1"
                                        value={newDepth}
                                        onChange={(e) => setNewDepth(parseInt(e.target.value, 10))}
                                        className="w-full accent-sage"
                                        data-testid="chat-depth-slider"
                                    />
                                    <div className="flex justify-between text-[10px] text-inkSoft/70 mt-1">
                                        <span>{t('chat.depth.1')}</span>
                                        <span>{t('chat.depth.2')}</span>
                                        <span>{t('chat.depth.3')}</span>
                                        <span>{t('chat.depth.4')}</span>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={applySettings} className="rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="apply-settings-btn">{t('chat.apply')}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Messages */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 md:px-8 py-6"
                data-testid="messages-area"
                onCopy={copyProtected ? (e) => {
                    // Belt-and-suspenders: block bubble-level copy that bypasses individual message handlers
                    // (e.g. Ctrl+A then Ctrl+C selecting the whole conversation).
                    e.preventDefault();
                    toast.message(t('chat.copy.blocked.title'), { description: t('chat.copy.blocked.body'), duration: 4500 });
                    setLimitReached(true);
                } : undefined}
            >
                <div className="max-w-2xl mx-auto space-y-4">
                    {messages.map(m => (
                        <Message key={m.message_id} m={m} copyProtected={copyProtected} onUpgradeNudge={() => setLimitReached(true)}/>
                    ))}
                    {sending && (
                        <div className="flex items-start gap-3" data-testid="typing-indicator">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-sage-soft shrink-0">
                                <img src={AI_AVATAR} alt="" className="w-full h-full object-cover opacity-80"/>
                            </div>
                            <div className="bg-sand/50 rounded-2xl rounded-bl-sm px-4 py-3">
                                <div className="flex gap-1">
                                    <span className="w-2 h-2 rounded-full bg-sage-dark/50 animate-pulse"/>
                                    <span className="w-2 h-2 rounded-full bg-sage-dark/50 animate-pulse" style={{animationDelay:'150ms'}}/>
                                    <span className="w-2 h-2 rounded-full bg-sage-dark/50 animate-pulse" style={{animationDelay:'300ms'}}/>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Input */}
            <div className="border-t border-sand bg-bone px-4 md:px-8 py-4 pb-20 md:pb-4">
                <div className="max-w-2xl mx-auto flex items-end gap-2">
                    <Textarea
                        value={text}
                        onChange={e => setText(e.target.value)}
                        placeholder={transcribing ? t('chat.input.transcribing') : recording ? t('chat.input.recording') : t('chat.input.placeholder')}
                        className="min-h-[52px] max-h-40 resize-none bg-white border-sand rounded-2xl focus-visible:ring-sage/30 focus-visible:border-sage"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        disabled={recording || transcribing}
                        data-testid="message-input"
                    />
                    <Button
                        onClick={recording ? stopRecording : startRecording}
                        disabled={transcribing || sending}
                        className={`rounded-full h-12 w-12 p-0 ${recording ? 'bg-crisis hover:bg-crisis-dark text-white animate-pulse' : 'bg-white border border-sand text-sage-dark hover:bg-sage/5'}`}
                        data-testid={recording ? 'stop-recording-btn' : 'start-recording-btn'}
                        title={recording ? t('chat.record.stop') : t('chat.record.start')}
                    >
                        {recording ? <Square className="w-4 h-4"/> : <Mic className="w-4 h-4"/>}
                    </Button>
                    <Button onClick={send} disabled={sending || !text.trim() || recording} className="rounded-full bg-sage hover:bg-sage-dark text-bone h-12 w-12 p-0" data-testid="send-btn">
                        <Send className="w-4 h-4"/>
                    </Button>
                </div>
                <ProgressStrip user={user} messages={messages}/>
                <StyleNudge messages={messages} onOpen={() => setSettingsOpen(true)}/>
                <SaveNudge user={user} messages={messages} onUpgrade={() => navigate('/app/konto')}/>
                <p className="text-[10px] text-inkSoft/60 text-center mt-2 px-4 leading-relaxed" data-testid="ai-disclosure">
                    {(session?.language || 'no') === 'en'
                        ? <>You are talking to an AI, not a human. Not a replacement for therapy or medical care. Crisis: <a href="tel:988" className="underline">988</a> · <Link to="/personvern" className="underline">Privacy</Link></>
                        : <>Du snakker med en KI, ikke et menneske. Ikke en erstatning for terapi eller helsehjelp. Krise: <a href="tel:11611" className="underline">116 123</a> · <Link to="/personvern" className="underline">Personvern</Link></>}
                </p>
            </div>

            {/* Limit modal */}
            <Dialog open={limitReached} onOpenChange={setLimitReached}>
                <DialogContent className="bg-bone max-w-md">
                    <DialogDescription className="sr-only">
                        {!user?.trial_used ? t('limit.trial.title') : t('limit.upgrade.title')}
                    </DialogDescription>
                    {!user?.trial_used ? (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">{t('limit.trial.title')}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <p className="text-inkSoft text-sm leading-relaxed">
                                    <span className="text-ink font-medium">{t('limit.trial.body.lead')}</span> {t('limit.trial.body.tail')}
                                </p>
                                <ul className="text-sm text-ink space-y-1">
                                    <li>✓ {t('limit.trial.feat1')}</li>
                                    <li>✓ {t('limit.trial.feat2')}</li>
                                    <li>✓ {t('limit.trial.feat3')}</li>
                                </ul>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setLimitReached(false)} data-testid="limit-close-btn" className="rounded-full border-sand">{t('limit.later')}</Button>
                                <Button onClick={async () => {
                                    try { await api.post('/trial/start'); await refresh(); setLimitReached(false); toast.success(t('limit.trial.activated')); }
                                    catch (e) { toast.error(e.response?.data?.detail || t('settings.trial.error')); }
                                }} className="rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="start-trial-btn">
                                    <Sparkles className="w-4 h-4 mr-2"/> {t('limit.start.trial')}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display" data-testid="limit-title">
                                    {t('limit.upgrade.title')}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <p className="text-inkSoft text-sm leading-relaxed" data-testid="limit-subtitle">
                                    {t('limit.upgrade.body')}
                                </p>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setLimitReached(false)} data-testid="limit-close-btn" className="rounded-full border-sand">{t('limit.later')}</Button>
                                <Button onClick={() => navigate('/app/konto')} className="rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="limit-upgrade-btn">
                                    <Sparkles className="w-4 h-4 mr-2"/> {t('limit.upgrade.see')}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Summary modal */}
            <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
                <DialogContent className="bg-bone max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">{t('chat.summary.title')}</DialogTitle>
                        <DialogDescription className="sr-only">{t('chat.summary.desc')}</DialogDescription>
                    </DialogHeader>
                    <div className="text-sm text-ink leading-relaxed" data-testid="summary-text">
                        {summary ? <FormattedText text={summary}/> : '–'}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setSummaryOpen(false)} className="rounded-full bg-sage text-bone" data-testid="summary-close-btn">{t('common.close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ProgressStrip({ user, messages }) {
    const { t } = useT();
    if (!user) return null;
    const plan = user.plan;
    if (plan === 'premium') return null;

    let label = '';
    let pct = 0;
    let urgent = false;

    if (plan === 'trial') {
        const hoursLeft = user.trial_hours_left ?? 72;
        urgent = hoursLeft <= 24;
        const h = Math.max(0, Math.round(hoursLeft));
        label = urgent ? t('progress.trial.urgent', { h }) : t('progress.trial.normal', { h });
        pct = Math.min(100, Math.max(0, (hoursLeft / 72) * 100));
    } else if (plan === 'basic') {
        const used = user.basic_minutes_used ?? 0;
        const limit = user.basic_minutes_limit ?? 30;
        const left = Math.max(0, limit - used);
        urgent = left <= 5 && left > 0;
        if (left <= 0) label = t('progress.basic.over');
        else label = t('progress.basic.left', { m: Math.max(1, Math.round(left)) });
        pct = Math.min(100, (used / limit) * 100);
    } else {
        // Free tier
        const used = user.message_count || 0;
        const limit = user.free_limit || 10;
        const left = Math.max(0, limit - used);
        label = left === 0 ? t('progress.free.over') : (left === 1 ? t('progress.free.left.one', { n: left }) : t('progress.free.left.many', { n: left }));
        pct = Math.min(100, (used / limit) * 100);
    }

    return (
        <div className="mt-2 max-w-2xl mx-auto" data-testid="progress-strip">
            <div className={`flex items-center justify-between text-[11px] mb-1 ${urgent ? 'text-crisis' : 'text-inkSoft'}`}>
                <span>{label}</span>
            </div>
            <div className="h-1 bg-sand rounded-full overflow-hidden">
                <div className={`h-full transition-all duration-500 ${urgent ? 'bg-crisis' : 'bg-sage'}`} style={{ width: `${pct}%` }}/>
            </div>
        </div>
    );
}

function StyleNudge({ messages, onOpen }) {
    const { t } = useT();
    const aiCount = messages.filter(m => m.role === 'assistant').length;
    // Show chip after every 5th AI message (5, 10, 15, ...). Skip the very first greeting.
    if (aiCount < 5 || aiCount % 5 !== 0) return null;
    return (
        <div className="mt-3 max-w-2xl mx-auto flex items-center justify-center" data-testid="style-nudge">
            <button
                onClick={onOpen}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sage/30 bg-sage/5 text-xs text-sage-dark hover:bg-sage/10 transition-colors"
                data-testid="style-nudge-chip"
            >
                <Settings2 className="w-3 h-3"/>
                {t('chat.style.nudge')}
            </button>
        </div>
    );
}

function SaveNudge({ user, messages, onUpgrade }) {
    const { t } = useT();
    const userMsgs = messages.filter(m => m.role === 'user').length;
    // Trigger around the "good conversation" moment - after 4-5 exchanges
    if (user?.plan === 'premium') return null;
    if (userMsgs < 4 || userMsgs > 6) return null;

    return (
        <div className="mt-3 max-w-2xl mx-auto flex items-start gap-3 bg-sage/5 border border-sage/20 rounded-xl px-3 py-2" data-testid="save-nudge">
            <span className="text-base shrink-0">🔖</span>
            <div className="flex-1 min-w-0">
                <p className="text-xs text-ink leading-snug">
                    {t('chat.save.nudge.q')} <span className="text-inkSoft">{t('chat.save.nudge.body')}</span>
                </p>
            </div>
            <button onClick={onUpgrade} className="shrink-0 text-xs font-medium text-sage-dark hover:underline" data-testid="save-nudge-cta">
                {t('chat.save.nudge.cta')}
            </button>
        </div>
    );
}

function Message({ m, copyProtected, onUpgradeNudge }) {
    const { t } = useT();
    const isUser = m.role === 'user';

    const blockCopy = (e) => {
        if (!copyProtected || isUser) return;
        e.preventDefault();
        toast.message(t('chat.copy.blocked.title'), { description: t('chat.copy.blocked.body'), duration: 4500 });
        if (onUpgradeNudge) onUpgradeNudge();
    };

    const protectedProps = (!isUser && copyProtected) ? {
        onCopy: blockCopy,
        onCut: blockCopy,
        onContextMenu: (e) => { e.preventDefault(); toast.message(t('chat.copy.blocked.title'), { description: t('chat.copy.blocked.body'), duration: 4500 }); if (onUpgradeNudge) onUpgradeNudge(); },
        onDragStart: (e) => e.preventDefault(),
        style: { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' },
        'data-copy-protected': 'true',
    } : {};

    return (
        <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''} animate-fade-up`} data-testid={`msg-${m.role}`}>
            {isUser ? (
                <div className="w-8 h-8 rounded-full bg-sand flex items-center justify-center text-xs font-medium text-inkSoft shrink-0">{t('chat.you')}</div>
            ) : (
                <div className="w-8 h-8 rounded-full overflow-hidden bg-sage-soft shrink-0">
                    <img src={AI_AVATAR} alt="" className="w-full h-full object-cover opacity-80"/>
                </div>
            )}
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 leading-relaxed ${isUser ? 'bg-sage text-bone rounded-br-sm whitespace-pre-wrap' : 'bg-white border border-sand text-ink rounded-bl-sm'}`} {...protectedProps}>
                {isUser ? m.content : (
                    <>
                        <FormattedText text={m.content}/>
                        <SpeakButton text={m.content} messageId={m.message_id}/>
                    </>
                )}
            </div>
        </div>
    );
}

function SpeakButton({ text, messageId }) {
    const { t } = useT();
    const [loading, setLoading] = useState(false);
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    const speak = async () => {
        if (playing && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setPlaying(false);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API}/voice/speak`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ text }),
            });
            if (!res.ok) throw new Error('TTS failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
            audio.onerror = () => { setPlaying(false); URL.revokeObjectURL(url); };
            await audio.play();
            setPlaying(true);
        } catch (e) {
            toast.error(t('chat.tts.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            onClick={speak}
            disabled={loading}
            className="mt-2 inline-flex items-center gap-1 text-xs text-inkSoft hover:text-sage-dark transition-colors"
            data-testid={`speak-btn-${messageId}`}
            title={playing ? t('chat.tts.stop') : t('chat.tts.read')}
        >
            {playing ? <StopCircle className="w-3.5 h-3.5"/> : <Volume2 className="w-3.5 h-3.5"/>}
            <span>{loading ? t('chat.tts.loading') : playing ? t('chat.tts.stop') : t('chat.tts.read')}</span>
        </button>
    );
}

// Render AI output with basic markdown: **bold**, *italic*, newlines, and heading-like emoji lines.
function FormattedText({ text }) {
    const lines = text.split('\n');
    return (
        <div className="space-y-2">
            {lines.map((line, idx) => {
                if (line.trim() === '') return <div key={idx} className="h-1"/>;
                // Heading heuristic: starts with an emoji followed by a space and some text,
                // and the whole line is short (< 80 chars). Render as section heading.
                const isHeading = /^[\p{Emoji}\uFE0F]/u.test(line) && line.length < 80 && !line.includes('.');
                return (
                    <p key={idx} className={isHeading ? 'font-display font-semibold text-ink mt-3 first:mt-0' : ''}>
                        {renderInline(line)}
                    </p>
                );
            })}
        </div>
    );
}

function renderInline(text) {
    const parts = [];
    let i = 0;
    let key = 0;
    while (i < text.length) {
        if (text.substr(i, 2) === '**') {
            const end = text.indexOf('**', i + 2);
            if (end !== -1) {
                parts.push(<strong key={key++} className="font-semibold text-ink">{text.slice(i + 2, end)}</strong>);
                i = end + 2;
                continue;
            }
        }
        if (text[i] === '*' && text[i + 1] !== ' ') {
            const end = text.indexOf('*', i + 1);
            if (end !== -1 && text[end - 1] !== ' ') {
                parts.push(<em key={key++}>{text.slice(i + 1, end)}</em>);
                i = end + 1;
                continue;
            }
        }
        // Accumulate plain run until next marker
        let j = i;
        while (j < text.length && text[j] !== '*') j++;
        parts.push(text.slice(i, j));
        i = j;
    }
    return parts;
}
